import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { Button } from './ui/button';
import WhatsAppButton from './WhatsAppButton';
import EmailButton from './EmailButton';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Determine navbar style based on page and scroll position
  const getNavbarStyle = () => {
    if (isScrolled || isMobileMenuOpen) {
      return 'bg-white shadow-lg py-3';
    }
    if (isHomePage) {
      return 'bg-transparent py-4';
    }
    return 'bg-white/95 backdrop-blur-sm py-4';
  };

  // Text color based on navbar background
  const getTextColor = (isActive) => {
    if (isScrolled || !isHomePage || isMobileMenuOpen) {
      return isActive ? 'text-cyan-600' : 'text-slate-700';
    }
    return isActive ? 'text-cyan-400' : 'text-white';
  };

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Services', path: '/services' },
    { name: 'Portfolio', path: '/portfolio' },
    { name: 'About', path: '/about' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Blog', path: '/blog' }
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${getNavbarStyle()}`}>
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <div className={`rounded-xl p-2 transition-all duration-300 ${
              isScrolled || !isHomePage || isMobileMenuOpen ? 'bg-transparent' : 'bg-white/90'
            }`}>
              <img 
                src="/images/emergent/logo.png" 
                alt="Paint It Better" 
                className="h-16 md:h-20 w-auto group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`font-medium transition-all duration-300 hover:text-cyan-400 relative group ${getTextColor(location.pathname === link.path)}`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-cyan-400 transition-all duration-300 group-hover:w-full ${
                  location.pathname === link.path ? 'w-full' : ''
                }`} />
              </Link>
            ))}
            <WhatsAppButton />
            <EmailButton />
            <Link to="/contact">
              <Button className="bg-cyan-500 hover:bg-cyan-600 text-white rounded-full px-6 transition-all duration-300 hover:scale-105">
                Free Quote
              </Button>
            </Link>
          </div>

          {/* Mobile: WhatsApp, email and menu buttons */}
          <div className="md:hidden flex items-center gap-2">
          <WhatsAppButton compact />
          <EmailButton compact />
          <button
            className={`p-2 transition-colors ${
              isScrolled || !isHomePage || isMobileMenuOpen ? 'text-slate-700 hover:text-cyan-600' : 'text-white hover:text-cyan-400'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-slate-200 pt-4 space-y-4 animate-in slide-in-from-top duration-300 bg-white rounded-b-xl">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block py-2 font-medium transition-colors ${
                  location.pathname === link.path ? 'text-cyan-600' : 'text-slate-700'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.name}
              </Link>
            ))}
            <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="w-full bg-cyan-600 hover:bg-cyan-700 text-white rounded-full">
                Free Quote
              </Button>
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;